import { useEffect, useState, useRef } from "react";
import { Clock, TrendingUp, BarChart3, Target, Zap, Brain, Activity, History, Users, ShieldAlert, ArrowLeft, Share } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GameStatistics } from "@/components/game-details/GameStatistics";
import { GameEvents } from "@/components/game-details/GameEvents";
import { HeadToHeadStats } from "@/components/game-details/HeadToHeadStats";
import { GameLineups } from "@/components/game-details/GameLineups";
import { GamePredictions } from "@/components/game-details/GamePredictions";
import { GameDiscipline } from "@/components/game-details/GameDiscipline";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import { useAnalysis } from "@/hooks/useAnalysis";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabasePublic } from "@/integrations/supabase/publicClient";
import { MobileLoading } from "@/components/ui/mobile-loading";
import {
  AnalysisPending,
  AnalysisProcessing,
  AnalysisError,
  TagsPills,
  HighlightsList,
  KeyFactsCard,
  DisciplineCard,
  KeyMinutesCard,
  PicksCard,
  ArticleRenderer,
  TechnicalPicksCard,
  convertN8NPicks,
  StatisticalSuggestionsCard,
  SG7ConfidenceMeter
} from "@/components/analysis";
import { OddsRow } from "@/lib/oddsEstimator";
import { AnalysisErrorBoundary } from "@/components/analysis/AnalysisErrorBoundary";
import { ShareModal } from "@/components/analysis/sg7/ShareModal";
import { MatchCopilotButton, MatchCopilotDrawer, MatchCopilotPanel, CopilotMiniFab } from "@/components/copilot";
import { LiveInsightsPanel } from "@/components/live-insights/LiveInsightsPanel";

// Helper to get locale string for date formatting
const getDateLocale = (lang: string): string => {
  const localeMap: Record<string, string> = {
    'en': 'en-GB', 'pt': 'pt-PT', 'pt-BR': 'pt-BR',
    'fr': 'fr-FR', 'es': 'es-ES', 'de': 'de-DE', 'it': 'it-IT',
  };
  return localeMap[lang] || 'en-GB';
};

interface Team {
  name: string;
  logo?: string;
}

interface GameData {
  id: string;
  homeTeam: Team;
  awayTeam: Team;
  league: string;
  leagueLogo?: string;
  date: string;
  time: string;
  status: "scheduled" | "finished" | "live";
  homeScore?: number;
  awayScore?: number;
  fixtureId?: number;
  homeTeamId?: string;
  awayTeamId?: string;
  homeTeamApiId?: number;
  awayTeamApiId?: number;
}

interface AnalysisDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  gameData: GameData | null;
  defaultTab?: string;
}

export function AnalysisDrawer({ isOpen, onClose, gameData, defaultTab = "analysis" }: AnalysisDrawerProps) {
  const { toast } = useToast();
  const { t: tDash, i18n } = useTranslation('dashboard');
  const { t: tAnalysis } = useTranslation('analysis');
  const [showShareModal, setShowShareModal] = useState(false);
  const [copilotMode, setCopilotMode] = useState<'closed' | 'overlay' | 'split' | 'minimized'>('closed');
  const [copilotWidth, setCopilotWidth] = useState(672);
  const [isMobile, setIsMobile] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Local state for copilot remaining messages (updated via callback from Drawer/Panel)
  const [gameRemainingMessages, setGameRemainingMessages] = useState(10);
  
  // Track mobile vs desktop
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Auto-focus scroll container when drawer opens (for keyboard/trackpad scrolling on laptops)
  useEffect(() => {
    if (isOpen && scrollContainerRef.current) {
      // Delay to ensure drawer animation completes and focus trap is ready
      const timer = setTimeout(() => {
        scrollContainerRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // iOS PWA scroll lock - prevents content behind drawer from scrolling
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Failsafe cleanup on unmount (route change, etc.)
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, []);
  
  // Fetch analysis data using the read-only hook
  const currentLang = i18n.language?.startsWith('pt') && !i18n.language.startsWith('pt-BR') ? 'pt' : (i18n.language || 'pt');
  const { analysis, loading: analysisLoading, translating } = useAnalysis(gameData?.fixtureId || null, { lang: currentLang });
  
  // Fetch full odds data for combo estimation (uses public client to avoid token issues)
  const { data: oddsData } = useQuery({
    queryKey: ['odds-full', gameData?.fixtureId],
    queryFn: async () => {
      if (!gameData?.fixtureId) return null;
      const { data, error } = await supabasePublic
        .from('odds')
        .select(`
          home_odds, draw_odds, away_odds,
          over_15_odds, under_15_odds,
          over_25_odds, under_25_odds,
          over_35_odds, under_35_odds,
          over_45_odds, under_45_odds,
          over_55_odds, under_55_odds,
          btts_yes_odds, btts_no_odds,
          dc_1x_odds, dc_12_odds, dc_x2_odds,
          home_over_25_odds, home_under_25_odds,
          draw_over_25_odds, draw_under_25_odds,
          away_over_25_odds, away_under_25_odds,
          h1_home_odds, h1_draw_odds, h1_away_odds,
          h1_over_05_odds, h1_over_15_odds,
          h1_under_05_odds, h1_under_15_odds
        `)
        .eq('fixture_id', gameData.fixtureId)
        .maybeSingle();
      
      if (error) {
        console.error('[AnalysisDrawer] Odds fetch error:', error.message);
        throw error;
      }
      return data as OddsRow | null;
    },
    enabled: !!gameData?.fixtureId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });

  // Fetch referee from games table (with on-demand Sportmonks fallback)
  const refereeBackfillRef = useRef<Set<number>>(new Set());
  const { data: refereeData, refetch: refetchReferee } = useQuery({
    queryKey: ['game-referee', gameData?.fixtureId],
    queryFn: async () => {
      if (!gameData?.fixtureId) return null;
      const { data } = await supabasePublic
        .from('games')
        .select('referee')
        .eq('fixture_id', gameData.fixtureId)
        .maybeSingle();
      return data?.referee ?? null;
    },
    enabled: !!gameData?.fixtureId,
    staleTime: 30 * 60 * 1000,
  });

  // On-demand referee fetch: if referee is null and we haven't tried yet for this fixture
  useEffect(() => {
    if (
      refereeData === null &&
      gameData?.fixtureId &&
      !refereeBackfillRef.current.has(gameData.fixtureId)
    ) {
      refereeBackfillRef.current.add(gameData.fixtureId);
      // Fire-and-forget: fetch referee from Sportmonks, then refetch local data
      import('@/lib/edgeFunctions').then(({ fetchRefereesSportmonks }) => {
        fetchRefereesSportmonks([gameData.fixtureId!])
          .then(() => {
            // Wait a moment for DB to update, then refetch
            setTimeout(() => refetchReferee(), 1500);
          })
          .catch((err) => console.warn('[AnalysisDrawer] Referee backfill failed:', err));
      });
    }
  }, [refereeData, gameData?.fixtureId, refetchReferee]);
  
  if (!gameData) return null;

  // Format date and time for sharing
  const formatShareDateTime = () => {
    const dateLocale = getDateLocale(i18n.language);
    const matchDate = new Date(gameData.date);
    
    const formattedDate = matchDate.toLocaleDateString(dateLocale, { 
      day: '2-digit', month: '2-digit', year: 'numeric' 
    });
    const formattedTime = matchDate.toLocaleTimeString(dateLocale, { 
      hour: '2-digit', minute: '2-digit' 
    });
    
    return { formattedDate, formattedTime };
  };

  const handleShare = async () => {
    const { formattedDate, formattedTime } = formatShareDateTime();
    const scoreText = gameData.status !== "scheduled" 
      ? `${gameData.homeScore ?? 0}-${gameData.awayScore ?? 0}` 
      : "vs";
    const shareText = `⚽ ${gameData.homeTeam.name} ${scoreText} ${gameData.awayTeam.name}\n🏆 ${gameData.league}\n📅 ${formattedDate} ${tDash('gameDetails.at')} ${formattedTime}`;
    const shareUrl = `${window.location.origin}/game/${gameData.fixtureId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${gameData.homeTeam.name} vs ${gameData.awayTeam.name}`,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Share cancelled:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n🔗 ${shareUrl}`);
        toast({
          title: tDash('gameDetails.linkCopied'),
          description: tDash('gameDetails.linkCopiedDescription'),
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: tDash('gameDetails.copyError'),
          description: tDash('gameDetails.copyErrorDescription'),
          variant: "destructive",
        });
      }
    }
  };

  // Helper to generate team abbreviation (first 3 letters uppercase)
  const getTeamAbbreviation = (teamName: string): string => {
    if (!teamName) return "TBD";
    return teamName
      .trim()
      .split(' ')[0] // Get first word
      .substring(0, 3) // First 3 characters
      .toUpperCase();
  };

  const isFinished = gameData.status === "finished";
  const isLive = gameData.status === "live";

  // Detect empty analysis (marked as ready but no content)
  const isEmptyAnalysis = analysis?.status === 'ready' && (
    !analysis.ai_output?.md_article && 
    !analysis.ai_output?.html_article &&
    (!analysis.ai_output?.picks || (Array.isArray(analysis.ai_output.picks) && analysis.ai_output.picks.length === 0))
  );

  // Render Analysis Tab Content based on status
  const renderAnalysisContent = () => {
    if (analysisLoading) {
      return <MobileLoading message="Loading analysis..." />;
    }

    // Empty analysis - show error with regenerate option
    if (isEmptyAnalysis) {
      return (
        <AnalysisError 
          message={tAnalysis('status.emptyAnalysis')}
          fixtureId={gameData.fixtureId}
          onRegenerate={() => {
            setTimeout(() => window.location.reload(), 1000);
          }}
        />
      );
    }

    // Ready state - show full analysis
    if (analysis?.status === 'ready' && analysis.ai_output) {
      const aiOutput = analysis.ai_output;
      const homeTeamName = gameData.homeTeam.name;
      const awayTeamName = gameData.awayTeam.name;

      return (
        <div className="space-y-4">
          {/* SG7 Confidence Meter - Premium header */}
          {typeof aiOutput.confidence === 'number' && (
            <SG7ConfidenceMeter 
              value={aiOutput.confidence <= 1 ? aiOutput.confidence * 100 : aiOutput.confidence}
              isLive={isLive}
            />
          )}
          
          {/* Tags */}
          {aiOutput.tags && <TagsPills tags={aiOutput.tags} />}
          {/* Highlights */}
          {aiOutput.highlights && aiOutput.highlights.length > 0 && (
            <HighlightsList highlights={aiOutput.highlights} />
          )}
          
          {/* Technical Picks - Merge structured picks with markdown-extracted picks */}
          {(aiOutput.picks?.length > 0 || aiOutput.md_article) && (
            <TechnicalPicksCard 
              picks={aiOutput.picks ? convertN8NPicks(aiOutput.picks) : undefined}
              mdArticle={aiOutput.md_article}
              oddsData={oddsData}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
            />
          )}
          
          {/* Statistical Suggestions Extra - support both field names and types (string or string[]) */}
          {(() => {
            const rawSugestoes = aiOutput.sugestoes_estatisticas_extra 
              || (aiOutput as Record<string, unknown>)['Sugestão Estatística Extra'];
            
            // Normalize: string → array, keep array as-is
            let sugestoesExtra: string[] | undefined;
            if (typeof rawSugestoes === 'string') {
              sugestoesExtra = [rawSugestoes];
            } else if (Array.isArray(rawSugestoes)) {
              sugestoesExtra = rawSugestoes;
            }
            
            return sugestoesExtra && sugestoesExtra.length > 0 ? (
              <StatisticalSuggestionsCard suggestions={sugestoesExtra} />
            ) : null;
          })()}
          
          {/* Grid: Key Facts + Picks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <KeyFactsCard 
              keyFacts={aiOutput.sidebars?.key_facts} 
              safeNumbers={analysis.safe_numbers}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
            />
            <PicksCard 
              picks={aiOutput.picks} 
              safeNumbers={analysis.safe_numbers}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
            />
          </div>
          
          {/* Grid: Discipline + Key Minutes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DisciplineCard 
              disciplina={aiOutput.sidebars?.disciplina}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
            />
            <KeyMinutesCard 
              minutosChave={aiOutput.sidebars?.minutos_chave}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
            />
          </div>
          
          {/* Full Article */}
          {(aiOutput.html_article || aiOutput.md_article) && (
            <ArticleRenderer 
              html={aiOutput.html_article} 
              markdown={aiOutput.md_article}
              referee={refereeData}
            />
          )}
          

          {/* Footer Note */}
          <div className="bg-[hsl(var(--sg7-surface-2))] p-3 rounded border border-[hsl(var(--sg7-stroke-subtle))]">
            <div className="flex items-center gap-2 text-xs text-[hsl(var(--sg7-text-muted))]">
              <Zap className="h-4 w-4 text-[hsl(var(--sg7-gold))]" />
              <span className="font-medium">
                Analysis generated by SG7 algorithm • Last update: {new Date(analysis.updated_at).toLocaleTimeString('en-GB')}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Processing state
    if (analysis?.status === 'processing') {
      return <AnalysisProcessing />;
    }

    // Error state
    if (analysis?.status === 'error') {
      return (
        <AnalysisError 
          message={analysis.error_message} 
          fixtureId={gameData.fixtureId}
          onRegenerate={() => {
            setTimeout(() => window.location.reload(), 1000);
          }}
        />
      );
    }

    // Pending state (default) - no analysis yet
    return <AnalysisPending matchDate={gameData.date} />;
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className={cn(
        // Mobile: full height - NO overflow-hidden to allow sticky to work
        "h-[100dvh] rounded-none bg-background flex flex-col",
        // Desktop: always full height, no gaps, no rounded corners
        "md:inset-0 md:mt-0 md:h-[100dvh] md:max-h-[100dvh] md:rounded-none"
      )}>
        {/* Mobile Header with Back, Title, Share */}
        <div className="flex items-center justify-between px-4 py-3 border-b md:hidden shrink-0">
          <DrawerClose asChild>
            <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors min-w-[60px]">
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
          </DrawerClose>
          <span className="text-sm font-semibold truncate max-w-[160px]">
            {gameData.homeTeam.name} vs {gameData.awayTeam.name}
          </span>
          <button 
            onClick={() => setShowShareModal(true)}
            className="flex items-center justify-end text-muted-foreground hover:text-foreground transition-colors min-w-[60px]"
          >
            <Share className="h-5 w-5" />
          </button>
        </div>
        
        <div 
          className={cn(
            "w-full flex-1 flex flex-col min-h-0 transition-all duration-300",
            copilotMode === 'split' && !isMobile ? "max-w-full" : "mx-auto md:max-w-4xl"
          )}
          style={{
            paddingRight: copilotMode === 'split' && !isMobile ? `${copilotWidth}px` : undefined,
          }}
        >
          <DrawerHeader className="sketch-border-bottom pb-2 md:pb-3 shrink-0">
            {/* Minimalist header with just status and score */}
            <div className="flex items-center justify-center gap-3 md:gap-4">
              {/* Status Badge */}
              <div
                className={cn(
                  "px-3 md:px-4 py-2 md:py-2.5 rounded sketch-border text-sm md:text-base font-bold whitespace-nowrap",
                  isLive && "bg-red-600 text-white animate-pulse",
                  isFinished && "bg-gray-medium text-background",
                  gameData.status === "scheduled" && "bg-blue-600 text-white"
                )}
              >
                {isLive ? "LIVE" : isFinished ? "FULL TIME" : "SCHEDULED"}
              </div>

              {/* Teams Score Card */}
              <div className="flex items-center gap-2 md:gap-3 sketch-border bg-paper-sketch px-3 md:px-4 py-2 md:py-2.5 rounded">
                {/* Home Team */}
                <div className="flex items-center gap-1.5 md:gap-2">
                  {gameData.homeTeam.logo && (
                    <img
                      src={gameData.homeTeam.logo}
                      alt={gameData.homeTeam.name}
                      className="w-6 h-6 md:w-8 md:h-8 object-contain"
                    />
                  )}
                  <span className="text-xs md:text-sm font-black text-foreground">
                    {getTeamAbbreviation(gameData.homeTeam.name)}
                  </span>
                </div>
                
                {/* Score or VS */}
                {isFinished || isLive ? (
                  <span className="text-base md:text-lg font-black text-primary px-1.5 md:px-2">
                    {gameData.homeScore ?? 0}-{gameData.awayScore ?? 0}
                  </span>
                ) : (
                  <span className="text-sm md:text-base font-bold text-gray-medium px-1.5 md:px-2">VS</span>
                )}
                
                {/* Away Team */}
                <div className="flex items-center gap-1.5 md:gap-2">
                  <span className="text-xs md:text-sm font-black text-foreground">
                    {getTeamAbbreviation(gameData.awayTeam.name)}
                  </span>
                  {gameData.awayTeam.logo && (
                    <img
                      src={gameData.awayTeam.logo}
                      alt={gameData.awayTeam.name}
                      className="w-6 h-6 md:w-8 md:h-8 object-contain"
                    />
                  )}
                </div>
              </div>
            </div>
            
            {/* Hidden drawer title for accessibility */}
            <DrawerTitle className="sr-only">
              {gameData.homeTeam.name} vs {gameData.awayTeam.name}
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              {gameData.league} • {formatShareDateTime().formattedDate} {tDash('gameDetails.at')} {formatShareDateTime().formattedTime}
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-4 md:px-6 pt-2 md:pt-3 flex flex-col flex-1 min-h-0">
            {/* Tabs Navigation */}
            <Tabs defaultValue={defaultTab} className="w-full flex flex-col flex-1 min-h-0">
              {/* Single scroll container - tabs bar is sticky inside */}
              <div 
                ref={scrollContainerRef}
                tabIndex={0}
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-[calc(env(safe-area-inset-bottom,0px)+80px)] focus:outline-none"
              >
                {/* Sticky Tabs Bar - inside scroll container so sticky works */}
                <div className="sticky top-0 z-30 bg-background pb-1 -mx-4 px-4 md:-mx-6 md:px-6">
                  <TabsList className="w-full justify-start overflow-x-auto flex-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] h-auto">
                    <TabsTrigger value="analysis" className="flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      <span>Analysis</span>
                    </TabsTrigger>
                    <TabsTrigger value="predictions" className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      <span>Prediction</span>
                    </TabsTrigger>
                    <TabsTrigger value="lineups" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>Lineups</span>
                    </TabsTrigger>
                    <TabsTrigger value="statistics" className="flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      <span>Stats</span>
                    </TabsTrigger>
                    <TabsTrigger value="events" className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      <span>Events</span>
                    </TabsTrigger>
                    <TabsTrigger value="discipline" className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4" />
                      <span>Discipline</span>
                    </TabsTrigger>
                    <TabsTrigger value="h2h" className="flex items-center gap-2">
                      <History className="h-4 w-4" />
                      <span>H2H</span>
                    </TabsTrigger>
                  </TabsList>
                </div>

                {/* Tab: Analysis */}
                <TabsContent value="analysis" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <AnalysisErrorBoundary fallbackMessage="Unable to display the analysis. Try reloading the page.">
                    {renderAnalysisContent()}
                  </AnalysisErrorBoundary>
                </TabsContent>

                {/* Tab: Predictions */}
                <TabsContent value="predictions" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <GamePredictions 
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                    homeTeamLogo={gameData.homeTeam.logo}
                    awayTeamLogo={gameData.awayTeam.logo}
                    leagueName={gameData.league}
                    leagueLogo={gameData.leagueLogo}
                    matchDate={gameData.date}
                    gameStatus={gameData.status}
                    homeScore={gameData.homeScore}
                    awayScore={gameData.awayScore}
                  />
                </TabsContent>

                {/* Tab: Lineups */}
                <TabsContent value="lineups" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <GameLineups 
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                    homeTeamLogo={gameData.homeTeam.logo}
                    awayTeamLogo={gameData.awayTeam.logo}
                    isLive={isLive}
                    isFinished={isFinished}
                  />
                </TabsContent>

                {/* Tab: Statistics */}
                <TabsContent value="statistics" className="mt-0 pt-2 md:pt-3 animate-fade-in space-y-4">
                  {/* Live Contextual Insights — only visible during live 0-0 */}
                  <LiveInsightsPanel
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamId={gameData.homeTeamId}
                    awayTeamId={gameData.awayTeamId}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                    homeScore={gameData.homeScore ?? 0}
                    awayScore={gameData.awayScore ?? 0}
                    isLive={isLive}
                  />
                  <GameStatistics 
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                    homeTeamId={gameData.homeTeamId}
                    awayTeamId={gameData.awayTeamId}
                  />
                </TabsContent>

                {/* Tab: Events */}
                <TabsContent value="events" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <GameEvents 
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamId={gameData.homeTeamId || ''}
                    awayTeamId={gameData.awayTeamId || ''}
                    inScrollableContainer={true}
                  />
                </TabsContent>

                {/* Tab: Discipline */}
                <TabsContent value="discipline" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <GameDiscipline 
                    fixtureId={gameData.fixtureId || 0}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                  />
                </TabsContent>

                {/* Tab: Head to Head */}
                <TabsContent value="h2h" className="mt-0 pt-2 md:pt-3 animate-fade-in">
                  <HeadToHeadStats 
                    teamHomeId={gameData.homeTeamId || ''}
                    teamAwayId={gameData.awayTeamId || ''}
                    teamHomeApiId={gameData.homeTeamApiId || 0}
                    teamAwayApiId={gameData.awayTeamApiId || 0}
                    homeTeamName={gameData.homeTeam.name}
                    awayTeamName={gameData.awayTeam.name}
                    homeTeamLogo={gameData.homeTeam.logo}
                    awayTeamLogo={gameData.awayTeam.logo}
                    inScrollableContainer={true}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
        
        {/* Desktop Close Button (visible on larger screens) */}
        <div className="hidden md:block absolute top-4 right-4">
          <DrawerClose asChild>
            <button className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-muted rounded">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </DrawerClose>
        </div>
        
        {/* Match Copilot SG7 - Inside DrawerContent for proper event handling */}
        {copilotMode !== 'split' && copilotMode !== 'minimized' && (
          <div className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-[60]">
            <MatchCopilotButton 
              onClick={() => {
                if (window.innerWidth >= 768) {
                  setCopilotMode('split');
                } else {
                  setCopilotMode('overlay');
                }
              }}
              remainingMessages={gameRemainingMessages}
              className="relative bottom-auto right-auto"
            />
          </div>
        )}
        
        {/* Mini FAB for minimized state */}
        {copilotMode === 'minimized' && (
          <CopilotMiniFab 
            onClick={() => setCopilotMode(isMobile ? 'overlay' : 'split')}
            hasMessages={false}
          />
        )}
        
        {/* Split-view panel (desktop only) - inside drawer */}
        {copilotMode === 'split' && gameData && (
          <MatchCopilotPanel
            fixtureId={gameData.fixtureId ?? null}
            gameStatus={gameData.status}
            homeTeam={gameData.homeTeam.name}
            awayTeam={gameData.awayTeam.name}
            onClose={() => setCopilotMode('closed')}
            onMinimize={() => setCopilotMode('minimized')}
            onWidthChange={(width) => setCopilotWidth(width)}
            onRemainingMessagesChange={setGameRemainingMessages}
          />
        )}
        </DrawerContent>
      </Drawer>
      
      {/* Match Copilot Overlay Drawer - OUTSIDE main Drawer (uses Portal) for mobile */}
      {isOpen && copilotMode === 'overlay' && gameData && (
        <MatchCopilotDrawer
          isOpen={true}
          onClose={() => setCopilotMode('closed')}
          fixtureId={gameData.fixtureId ?? null}
          gameStatus={gameData.status}
          homeTeam={gameData.homeTeam.name}
          awayTeam={gameData.awayTeam.name}
          onRemainingMessagesChange={setGameRemainingMessages}
        />
      )}
    </>
  );
}
